// CHANGE v2 §11: editar selfie anterior — mesmo shell ritual (Ativos → Dívidas → Confirmar).
// .pen: nU8QY/j07Yv (Selfie · Edição · Ativos), pbg46/k14wzN (Selfie ·
// Edição · Dívidas), OVQ8Q/FUVIC (Selfie · Edição · Confirmação)
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DeleteSelfieDialog } from '../../components/snapshots/DeleteSelfieDialog';
import { SelfieAssetsStep } from '../../components/snapshots/ritual/SelfieAssetsStep';
import { SelfieConfirmStep } from '../../components/snapshots/ritual/SelfieConfirmStep';
import { SelfieDebtsStep } from '../../components/snapshots/ritual/SelfieDebtsStep';
import { SelfieLiveNetWorthBar } from '../../components/snapshots/ritual/SelfieLiveNetWorthBar';
import { WizardSegmentedProgress, WizardSkeleton, WizardStepFooter } from '../../components/wizard';
import {
  useDeleteSnapshotMutation,
  useSnapshotDetailQuery,
  useSnapshotsListQuery,
  useUpdateAccountSnapshotMutation,
  useUpdateSnapshotMutation,
} from '../../features/snapshots/queries';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useMobilePageHeaderTitle } from '../../store/MobilePageHeaderContext';
import pt from '../../locales/pt';
import {
  computeLiveParts,
  formatDecimalForInput,
  monthLongLabel,
} from '../../lib/selfieDisplayUtils';
import { paths } from '../../routes/paths';
import { parsePtNumber } from '../../utils/money';

const STEPS = ['assets', 'debts', 'confirm'];

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

function capitalizeMonth(label) {
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Bug real encontrado em QA: valores crus da API (ex. "3000.000000") ficavam
// sem formatar e transbordavam o input — usa o mesmo formatador que o Ritual
// já usa para pré-preencher (formatDecimalForInput), nunca String() directo.
function buildEditEntries(snapshot) {
  const accountEntries = {};
  for (const row of snapshot?.account_snapshots || []) {
    const isCrypto = row.account_category === 'crypto';
    accountEntries[row.account] = {
      balance_native: formatDecimalForInput(row.balance_native, {
        maxDecimals: isCrypto ? 8 : 2,
      }),
      price_per_unit:
        row.price_per_unit !== undefined && row.price_per_unit !== null
          ? formatDecimalForInput(row.price_per_unit)
          : '',
      source: row.source || 'manual',
    };
  }

  const liabilityEntries = {};
  for (const row of snapshot?.liability_snapshots || []) {
    liabilityEntries[row.liability] = {
      remaining_balance_eur: formatDecimalForInput(row.remaining_balance_eur),
    };
  }

  return { accountEntries, liabilityEntries };
}

export function SelfieEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: snapshot, isPending, isFetching } = useSnapshotDetailQuery(id);
  const { data: allSnapshots = [] } = useSnapshotsListQuery();
  const snapshotShell = useQueryShellState({ data: snapshot, isPending, isFetching });
  const updateSnapshot = useUpdateSnapshotMutation();
  const updateAccountSnapshot = useUpdateAccountSnapshotMutation();
  const deleteSnapshot = useDeleteSnapshotMutation();

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [snapshotDate, setSnapshotDate] = useState('');
  const [accountEntries, setAccountEntries] = useState({});
  const [liabilityEntries, setLiabilityEntries] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const previousSnapshot = useMemo(() => {
    if (!snapshot) return null;
    const sorted = [...allSnapshots].sort((a, b) => {
      if (a.reference_year !== b.reference_year) {
        return b.reference_year - a.reference_year;
      }
      return b.reference_month - a.reference_month;
    });
    const index = sorted.findIndex((row) => row.id === snapshot.id);
    return index >= 0 ? sorted[index + 1] : null;
  }, [allSnapshots, snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    setSnapshotDate(snapshot.snapshot_date || '');
    const { accountEntries: nextAccounts, liabilityEntries: nextLiabilities } =
      buildEditEntries(snapshot);
    setAccountEntries(nextAccounts);
    setLiabilityEntries(nextLiabilities);
  }, [snapshot]);

  const accounts = useMemo(
    () =>
      (snapshot?.account_snapshots || []).map((row) => ({
        id: row.account,
        name: row.account_name,
        category: row.account_category || 'bank_account',
        currency: row.balance_currency || 'EUR',
      })),
    [snapshot],
  );

  const liabilities = useMemo(
    () =>
      (snapshot?.liability_snapshots || []).map((row) => ({
        id: row.liability,
        name: row.liability_name,
        type: row.liability_type,
        classification: row.liability_classification || 'neutral',
      })),
    [snapshot],
  );

  const previousAccountEntries = useMemo(() => {
    const map = {};
    if (previousSnapshot?.account_snapshots) {
      for (const row of previousSnapshot.account_snapshots) {
        map[row.account] = {
          balance_native: row.balance_native,
          price_per_unit: row.price_per_unit,
        };
      }
    }
    return map;
  }, [previousSnapshot]);

  const previousLiabilityEntries = useMemo(() => {
    const map = {};
    if (previousSnapshot?.liability_snapshots) {
      for (const row of previousSnapshot.liability_snapshots) {
        map[row.liability] = {
          remaining_balance_eur: row.remaining_balance_eur,
        };
      }
    }
    return map;
  }, [previousSnapshot]);

  const liveParts = useMemo(
    () =>
      computeLiveParts({
        accounts,
        liabilities,
        accountEntries,
        liabilityEntries,
        previousAccountEntries,
      }),
    [accounts, liabilities, accountEntries, liabilityEntries, previousAccountEntries],
  );

  const liveDelta =
    previousSnapshot?.net_worth_eur != null
      ? liveParts.net - Number(previousSnapshot.net_worth_eur)
      : null;

  const stepKey = STEPS[stepIndex];
  const showLiveBar = stepKey === 'assets' || stepKey === 'debts';
  const monthTitle = capitalizeMonth(
    monthLongLabel(snapshot?.reference_month || 1).toLowerCase(),
  );
  const wizardNavTitle = format(pt.snapshots.edit.wizardTitle, {
    month: snapshot ? monthTitle : '…',
  });
  useMobilePageHeaderTitle(wizardNavTitle);
  const monthYear = snapshot
    ? format(pt.snapshots.history.monthYearFormat, {
        month: monthLongLabel(snapshot.reference_month),
        year: snapshot.reference_year,
      })
    : '';

  const goTo = (nextIndex) => {
    setDirection(nextIndex > stepIndex ? 'forward' : 'back');
    setStepIndex(nextIndex);
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      navigate(paths.selfies);
      return;
    }
    goTo(stepIndex - 1);
  };

  const handleSave = async () => {
    if (snapshotDate !== snapshot.snapshot_date) {
      await updateSnapshot.mutateAsync({
        id,
        payload: { snapshot_date: snapshotDate },
      });
    }

    for (const row of snapshot.account_snapshots || []) {
      const entry = accountEntries[row.account];
      const current = String(row.balance_native ?? '');
      const next = entry?.balance_native ?? '';
      if (next !== current) {
        await updateAccountSnapshot.mutateAsync({
          id: row.id,
          payload: {
            balance_native: parsePtNumber(next) ?? next,
            price_per_unit:
              entry?.price_per_unit !== undefined && entry.price_per_unit !== ''
                ? parsePtNumber(entry.price_per_unit) ?? entry.price_per_unit
                : undefined,
          },
        });
      }
    }

    toast.success(pt.snapshots.edit.savedToast);
    navigate(paths.selfies);
  };

  const handlePrimary = () => {
    if (stepKey === 'confirm') {
      handleSave();
      return;
    }
    goTo(Math.min(stepIndex + 1, STEPS.length - 1));
  };

  const handleConfirmDelete = async () => {
    await deleteSnapshot.mutateAsync(id);
    navigate(paths.selfies, { replace: true });
  };

  const saving = updateSnapshot.isPending || updateAccountSnapshot.isPending;
  const deleting = deleteSnapshot.isPending;
  const stepPhaseLabel = pt.snapshots.edit.stepLabels[stepKey];

  if (snapshotShell.showBlockingSkeleton) {
    return (
      <WizardSkeleton
        appChrome
        wizardTitle={wizardNavTitle}
        step={1}
        total={STEPS.length}
        showProgress
        loading
      >
        <SectionSkeleton variant="card-grid" className="max-w-lg" />
      </WizardSkeleton>
    );
  }

  if (!snapshot) return null;

  return (
    <>
      <WizardSkeleton
        appChrome
        wizardTitle={wizardNavTitle}
        onBack={handleBack}
        showBack
        direction={direction}
        loading={saving}
        showProgress
        progress={
          <WizardSegmentedProgress
            step={stepIndex + 1}
            total={STEPS.length}
            showPercent
            phaseLabel={stepPhaseLabel}
          />
        }
        footer={
          <WizardStepFooter
            primaryLabel={
              stepKey === 'confirm'
                ? pt.snapshots.ritual.confirm.ctaEdit
                : pt.snapshots.ritual.balances.cta
            }
            onPrimary={handlePrimary}
            loading={saving}
            destructiveLabel={
              stepKey === 'confirm' ? pt.snapshots.edit.deleteLabel : undefined
            }
            onDestructive={
              stepKey === 'confirm' ? () => setDeleteDialogOpen(true) : undefined
            }
            destructiveDisabled={deleting || saving}
          />
        }
      >
        {showLiveBar ? (
          <SelfieLiveNetWorthBar
            className="mb-3"
            total={liveParts.net}
            delta={liveDelta}
            previousMonthLabel={
              previousSnapshot?.reference_month
                ? monthLongLabel(previousSnapshot.reference_month)
                : null
            }
            hasPrevious={previousSnapshot?.net_worth_eur != null}
          />
        ) : null}

        {stepKey === 'assets' ? (
          <SelfieAssetsStep
            accounts={accounts}
            accountEntries={accountEntries}
            previousAccountEntries={previousAccountEntries}
            onAccountEntriesChange={setAccountEntries}
            title={format(pt.snapshots.edit.title, { month: monthTitle })}
            subtitle={pt.snapshots.edit.subtitle}
          />
        ) : null}

        {stepKey === 'debts' ? (
          <SelfieDebtsStep
            liabilities={liabilities}
            liabilityEntries={liabilityEntries}
            previousLiabilityEntries={previousLiabilityEntries}
            onLiabilityEntriesChange={setLiabilityEntries}
            title={format(pt.snapshots.edit.debtsTitle, { month: monthTitle })}
            subtitle={format(pt.snapshots.edit.debtsSubtitle, { month: monthTitle })}
          />
        ) : null}

        {stepKey === 'confirm' ? (
          <SelfieConfirmStep
            assetsSum={liveParts.assets}
            debtsSum={liveParts.debt}
            netWorth={liveParts.net}
            showDateFields
            snapshotDate={snapshotDate}
            onSnapshotDateChange={setSnapshotDate}
            title={pt.snapshots.edit.confirmTitle}
            subtitle={format(pt.snapshots.edit.confirmSubtitle, { month: monthTitle })}
            adjustDatesLabel={pt.snapshots.edit.adjustDate}
          />
        ) : null}
      </WizardSkeleton>

      <DeleteSelfieDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        monthYear={monthYear}
        onConfirm={handleConfirmDelete}
        isPending={deleting}
      />
    </>
  );
}

export default SelfieEdit;
